import { useNavigate, useParams } from "react-router-dom";

import { BaseballField } from "@/components/field/BaseballField";
import { OptionButton } from "@/components/scenario/OptionButton";
import { ScenarioCard } from "@/components/scenario/ScenarioCard";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useProgressReview } from "@/hooks/use-progress";
import { getErrorMessage } from "@/utils/error-message";
import { getReviewOptionState } from "@/utils/option-state";

export function Review() {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();
  const { data: review, isLoading, error } = useProgressReview(scenarioId!);

  if (isLoading) return <LoadingSpinner message="Loading review..." />;
  if (error) return <ErrorMessage message={getErrorMessage(error)} />;
  if (!review) return <ErrorMessage message="Review not found" />;

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/progress")}
        className="flex min-h-[44px] items-center gap-1 text-sm font-medium text-navy-600 hover:text-navy-800"
      >
        &larr; Back to History
      </button>

      <div className="overflow-hidden rounded-xl shadow-sm">
        <BaseballField
          fieldState={review.scenario.fieldState}
          className="block w-full"
        />
      </div>

      <ScenarioCard scenario={review.scenario}>
        {review.scenario.options.map((option) => (
          <OptionButton
            key={option.id}
            label={option.text}
            state={getReviewOptionState(option.id, review.selectedOptionId, review.correctOptionId)}
            disabled
          />
        ))}
      </ScenarioCard>

      <Card className={review.correct ? "border-2 border-green-400 bg-green-50" : "border-2 border-red-400 bg-red-50"}>
        <div className="mb-3 flex items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold text-white ${review.correct ? "bg-green-500" : "bg-red-500"}`}>
            {review.correct ? "✓" : "✗"}
          </span>
          <span className={`text-lg font-bold ${review.correct ? "text-green-700" : "text-red-700"}`}>
            {review.correct ? "You got it right!" : "Incorrect"}
          </span>
        </div>
        <p className={`mb-4 text-sm ${review.correct ? "text-green-800" : "text-red-800"}`}>
          {review.explanation ?? "No explanation available."}
        </p>
        <p className="text-xs text-gray-500">
          Answered on {new Date(review.answeredAt).toLocaleDateString()}
        </p>
      </Card>

      <Button variant="secondary" onClick={() => navigate("/progress")} className="w-full">
        Back to History
      </Button>
    </div>
  );
}
